<script lang="ts" setup>
import { ref, getCurrentInstance, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import {
  CheckboxFieldValue,
  RequestFieldValue,
  TextFieldValue,
  RadioFieldValue,
  DatepickerFieldValue,
  DropdownFieldValue
} from 'src/pages/request_show/store/request_state_declarations'
import RequestFieldInput from '../../../vue_shared/components/RequestFieldInput.vue'

interface RequestFieldEditModalProps {
  field: RequestFieldValue
}

const props = defineProps<RequestFieldEditModalProps>()
const emit = defineEmits(['close'])

const store = useStore()
const instance = getCurrentInstance()
const globalProperties = instance?.appContext.config.globalProperties

const fieldValue = ref<
  | TextFieldValue
  | CheckboxFieldValue
  | RadioFieldValue
  | DatepickerFieldValue
  | DropdownFieldValue
>(props.field?.value)
const updating = ref(false)

const handleClose = () => {
  emit('close')
}

const handleSave = async () => {
  if (!props.field) return

  try {
    updating.value = true
    const requestId = store.state.request_show.request.pretty_id

    if (props.field.id) {
      await store.dispatch('request_show/updateFieldValue', {
        id: props.field.id,
        value: fieldValue.value,
        requestId
      })
      globalProperties?.$toasted.success('Successfully updated field value.')
    } else {
      await store.dispatch('request_show/createFieldValue', {
        requestFieldVersionId: props.field.request_field_version.id,
        value: fieldValue.value,
        requestId
      })
      globalProperties?.$toasted.success('Successfully added field value.')
    }

    await store.dispatch('request_show/fetchRequest', requestId)
    handleClose()
  } catch (error) {
    handleError(error)
  } finally {
    updating.value = false
  }
}

const handleError = (error: unknown) => {
  let errorMessage =
    'There has been an error saving the field value. Please refresh the page and try again.'
  if (error instanceof Error) {
    errorMessage = `Error saving field value: ${error.message}`
  }
  globalProperties?.$toasted.error(errorMessage)
}
</script>

<template>
  <NrModal
    :show="true"
    :title="props.field.id ? 'Edit field' : 'Add field value'"
    primaryButtonText="Continue"
    secondaryButtonText="Cancel"
    :hasSecondaryButton="true"
    :disableButtons="updating"
    @on-primary-click="handleSave"
    @on-secondary-click="handleClose"
    @on-close-click="handleClose"
  >
    <div class="fields-note">
      Fields with <span class="required-indicator">*</span> are required
    </div>
    <RequestFieldInput
      :definition="{
        field_type: props.field.field_type,
        type_settings: props.field.type_settings
      }"
      :modelValue="fieldValue"
      @update:modelValue="fieldValue = $event"
    />
  </NrModal>
</template>

<style lang="scss" scoped>
.field-header {
  margin-bottom: 1.5rem;
}
.field-history {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}
.mt-4 {
  margin-top: 1rem;
}
.required-indicator {
  color: #1b34ff;
  margin-left: 2px;
}
.fields-note {
  font-style: italic;
  color: #6c757d;
  margin-bottom: 1.5rem;
}
</style>
